import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMemo, useState, type ReactElement } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import MarketingStationLayout from 'app/shared-components/MarketingStationLayout';

import useHeaderActions from 'app/hooks/useHeaderActions';
import useNavigationHeader from 'app/hooks/useNavigationHeader';
import useHeaderTitle from 'app/hooks/useHeaderTitle';

import ErrorState from 'app/shared-components/common/ErrorState';

import type { Section } from './hooks/useGetPageDetails';
import { useGetPageDetails } from './hooks/useGetPageDetails';
import { useReorderSections } from './hooks/useReorderSections';
import { useSetSectionVisibility } from './hooks/useSetSectionVisibility';
import { useDeleteSection } from './hooks/useDeleteSection';
import { DeleteSectionDialog } from './DeleteSectionDialog';
import { SectionVisibilityDialog } from './SectionVisibilityDialog';
import { SectionTypeIcon } from './SectionTypeIcon';
import { SectionTypePicker } from './SectionTypePicker';
import { useGetGroupedSectionTypes } from './hooks/useGetGroupedSectionTypes';
import usePlusWebsites from '../../../hooks/usePlusWebsites';
import { usePublishWebsite } from '../../../hooks/usePublishWebsite';
import { pagePreviewPath, websiteDetailsPath } from '../../../paths';
import { UnpublishedChangesIndicator } from '../../../components/UnpublishedChangesIndicator';
import { useGetPages } from '../hooks/useGetPages';

const PageDetailsPage = (): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pageId } = useParams<{ pageId: string }>();
  const [searchParams] = useSearchParams();
  const websiteId = searchParams.get('websiteId') ?? '';
  const organisationId = searchParams.get('organisationId') ?? '';

  // Coming from the pages list the name arrives in the navigation state, but a reload or
  // a return from a section editor has none — so the cached pages list is the real source.
  const { pages } = useGetPages({ websiteId });
  const pageNameFromState = (location.state as { pageName?: string } | null)?.pageName;
  const pageName = pages.find((page) => page.id === pageId)?.name ?? pageNameFromState ?? 'Page';

  const { sections, isLoading, isError, error } = useGetPageDetails({
    websiteId,
    pageId: pageId ?? '',
  });
  const {
    groups: sectionTypeGroups,
    isLoading: isSectionTypesLoading,
    isError: isSectionTypesError,
  } = useGetGroupedSectionTypes(organisationId);

  const reorderSections = useReorderSections({ websiteId, pageId: pageId ?? '' });
  const isReordering = reorderSections.isLoading;

  const setSectionVisibility = useSetSectionVisibility({ websiteId, pageId: pageId ?? '' });
  const togglingSectionId = setSectionVisibility.isLoading
    ? setSectionVisibility.variables?.sectionId
    : undefined;

  const deleteSection = useDeleteSection({ websiteId, pageId: pageId ?? '' });
  const [sectionToDelete, setSectionToDelete] = useState<Section | null>(null);
  const [sectionToToggle, setSectionToToggle] = useState<Section | null>(null);

  const { plusWebsites } = usePlusWebsites();
  const website = plusWebsites.find((w) => w.id === websiteId) ?? null;
  const { mutate: publishWebsite, isPending: isPublishing } = usePublishWebsite();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (isReordering) return;

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sections.findIndex((section) => section.id === active.id);
    const newIndex = sections.findIndex((section) => section.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(sections, oldIndex, newIndex);
    reorderSections.mutate({ sectionIds: reordered.map((section) => section.id) });
  };

  // Adding and editing sections pushes entries onto the history stack, so stepping back
  // through it would trap the user in the section editors instead of leaving the page.
  useNavigationHeader({
    title: 'Back',
    navigateTo: websiteDetailsPath(searchParams),
    backToHistory: false,
    replace: true,
  });

  useHeaderTitle(pageName);

  const headerActions = useMemo(
    () => [
      {
        title: 'Preview website',
        type: 'button' as const,
        callback: () =>
          window.open(
            pagePreviewPath({ organisationId, websiteId, pageId: pageId ?? '' }),
            '_blank',
            'noopener,noreferrer'
          ),
        disabled: !websiteId,
      },
      {
        title: isPublishing ? 'Publishing…' : 'Publish',
        type: 'button' as const,
        callback: () => {
          if (websiteId) publishWebsite(websiteId);
        },
        disabled: !websiteId || !website?.hasUnpublishedChanges || isPublishing,
      },
    ],
    [organisationId, websiteId, pageId, website, isPublishing, publishWebsite]
  );

  useHeaderActions(headerActions);

  if (isError) {
    return (
      <ErrorState
        title="Failed to load page details"
        subtitle={error instanceof Error ? error.message : 'Try again later'}
      />
    );
  }

  return (
    <MarketingStationLayout className="vtwo:flex vtwo:flex-col vtwo:md:flex-row vtwo:h-full vtwo:overflow-hidden">
      <Box className="vtwo:w-full vtwo:md:w-[25%] vtwo:shrink-0 vtwo:bg-white vtwo:overflow-y-auto vtwo:p-5 vtwo:md:p-11 vtwo:flex vtwo:flex-col vtwo:gap-6 vtwo:border-b vtwo:border-stone-200 vtwo:md:border-b-0 vtwo:md:border-r">
        <SectionTypePicker
          groups={sectionTypeGroups}
          isLoading={isSectionTypesLoading}
          isError={isSectionTypesError}
          onAdd={(sectionType) =>
            navigate(
              {
                pathname: `/plus-websites/pages/${pageId ?? ''}/sections/create`,
                search: new URLSearchParams({ organisationId, websiteId }).toString(),
              },
              {
                state: {
                  sectionDisplayName: sectionType.displayName,
                  sectionTypeId: sectionType.id,
                  sectionTypeVersion: sectionType.version,
                },
              }
            )
          }
        />
      </Box>

      <Box className="vtwo:flex-1 vtwo:overflow-y-auto vtwo:bg-plus-muted vtwo:p-5 vtwo:md:p-8 vtwo:border-b vtwo:border-stone-200 vtwo:md:border-b-0 vtwo:md:border-l">
        <Box className="vtwo:w-full vtwo:max-w-2xl vtwo:mx-auto vtwo:flex vtwo:flex-col vtwo:gap-3">
          <Box className="vtwo:flex vtwo:items-center vtwo:justify-between vtwo:gap-4">
            <Typography variant="subtitle2" className="vtwo:font-bold">
              Page structure
            </Typography>
            {website?.hasUnpublishedChanges && <UnpublishedChangesIndicator />}
          </Box>

          {isLoading && (
            <Box className="vtwo:flex vtwo:justify-center vtwo:py-8">
              <CircularProgress size={24} />
            </Box>
          )}

          {!isLoading && sections.length === 0 && (
            <Typography variant="body2" className="vtwo:text-gray-500">
              No sections defined.
            </Typography>
          )}

          {!isLoading && sections.length > 0 && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sections.map((section) => section.id)}
                strategy={verticalListSortingStrategy}
              >
                {sections.map((section) => (
                  <SectionRow
                    key={section.id}
                    section={section}
                    isReorderDisabled={isReordering}
                    isTogglingVisibility={togglingSectionId === section.id}
                    isDeleting={deleteSection.isLoading && sectionToDelete?.id === section.id}
                    onDelete={() => setSectionToDelete(section)}
                    onToggleVisibility={() => setSectionToToggle(section)}
                    onModify={() =>
                      navigate({
                        pathname: `/plus-websites/pages/${pageId ?? ''}/sections/${
                          section.id
                        }/edit`,
                        search: new URLSearchParams({ organisationId, websiteId }).toString(),
                      })
                    }
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </Box>
      </Box>

      <DeleteSectionDialog
        open={sectionToDelete !== null}
        sectionName={sectionToDelete?.sectionType.displayName ?? ''}
        isDeleting={deleteSection.isLoading}
        onClose={() => setSectionToDelete(null)}
        onConfirm={() => {
          if (!sectionToDelete) return;
          deleteSection.mutate(
            { sectionId: sectionToDelete.id },
            { onSuccess: () => setSectionToDelete(null) }
          );
        }}
      />

      <SectionVisibilityDialog
        open={sectionToToggle !== null}
        sectionName={sectionToToggle?.sectionType.displayName ?? ''}
        isVisible={sectionToToggle?.isVisible ?? false}
        isUpdating={setSectionVisibility.isLoading}
        onClose={() => setSectionToToggle(null)}
        onConfirm={() => {
          if (!sectionToToggle) return;
          setSectionVisibility.mutate(
            { sectionId: sectionToToggle.id, isVisible: !sectionToToggle.isVisible },
            { onSuccess: () => setSectionToToggle(null) }
          );
        }}
      />
    </MarketingStationLayout>
  );
};

interface SectionRowProps {
  section: Section;
  onModify: () => void;
  onToggleVisibility: () => void;
  onDelete: () => void;
  isReorderDisabled: boolean;
  isTogglingVisibility: boolean;
  isDeleting: boolean;
}

function SectionRow({
  section,
  onModify,
  onToggleVisibility,
  onDelete,
  isReorderDisabled,
  isTogglingVisibility,
  isDeleting,
}: SectionRowProps): ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
    disabled: isReorderDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const dragHandleCursor = isReorderDisabled
    ? 'vtwo:cursor-not-allowed'
    : 'vtwo:cursor-grab active:vtwo:cursor-grabbing';
  const draggingStyle = isDragging ? 'vtwo:shadow-lg vtwo:opacity-90' : '';
  const sectionBackground = section.isVisible ? 'vtwo:bg-white' : 'vtwo:bg-plus-muted';
  const visibilityIconColour = section.isVisible ? 'vtwo:text-gray-600' : 'vtwo:text-gray-300';
  const visibilityLabel = section.isVisible ? 'Hide section' : 'Show section';
  // Not every section carries a content title, and a blank one must not leave an empty line.
  const sectionTitle = section.contentTitle?.trim();

  return (
    <Box
      ref={setNodeRef}
      style={style}
      className={`vtwo:flex vtwo:justify-between vtwo:items-center vtwo:px-5 vtwo:py-3 vtwo:rounded-xl vtwo:border-2 vtwo:border-stone-200 ${sectionBackground} ${draggingStyle}`}
    >
      <Box className="vtwo:flex vtwo:items-center vtwo:gap-4">
        <Box
          {...attributes}
          {...listeners}
          aria-label={`Drag to reorder ${section.sectionType.displayName}`}
          className={`vtwo:flex vtwo:items-center ${dragHandleCursor}`}
        >
          <DragIndicatorIcon className="vtwo:text-gray-400 vtwo:text-[20px]" />
        </Box>
        <SectionTypeIcon iconUrl={section.sectionType.iconUrl} size="lg" />
        <Box className="vtwo:flex vtwo:flex-col">
          {sectionTitle && (
            <Typography className="vtwo:text-typography-primary">{sectionTitle}</Typography>
          )}
          <Typography variant="body2" className="vtwo:font-bold">
            {section.sectionType.displayName}
          </Typography>
        </Box>
      </Box>
      <Box className="vtwo:flex vtwo:items-center vtwo:gap-4">
        <Tooltip title="Show/Hide section on page" describeChild>
          <span>
            <IconButton
              size="small"
              onClick={onToggleVisibility}
              disabled={isTogglingVisibility}
              aria-label={visibilityLabel}
              aria-pressed={section.isVisible}
            >
              <VisibilityIcon className={`vtwo:text-[20px] ${visibilityIconColour}`} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete section from page" describeChild>
          <span>
            <IconButton
              size="small"
              onClick={onDelete}
              disabled={isDeleting}
              aria-label={`Delete ${section.sectionType.displayName}`}
            >
              <DeleteOutlineIcon className="vtwo:text-[20px] vtwo:text-gray-600" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Modify section content" describeChild>
          <Button
            variant="outlined"
            onClick={onModify}
            sx={{
              borderRadius: '8px',
              color: '#6448F1',
              borderColor: '#6448F1',
              '&:hover': { borderColor: '#5338E1', backgroundColor: 'rgba(100, 72, 241, 0.04)' },
            }}
          >
            Modify
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default PageDetailsPage;
